import Image from "next/image";
import Link from "@/components/Link";
import type { HomePackage } from "@/lib/navigation";

import { AppRoutes } from "@/lib/routeResolver";
import styles from "./package-card.module.css";

export default function PackageCard({
  pack,
  isTravelIdea = false,
}: {
  pack: HomePackage;
  /** Travel ideas are inspiration, not a priced product — hide cities and price. */
  isTravelIdea?: boolean;
}) {
  const href = AppRoutes.package(pack.pageURL);
  const showCities = !isTravelIdea && pack.cities.length > 0;

  // "x Days / y Nights"; omit whichever part is 0.
  const duration = [
    pack.days > 0 ? `${pack.days} Day${pack.days === 1 ? "" : "s"}` : "",
    pack.nights > 0 ? `${pack.nights} Night${pack.nights === 1 ? "" : "s"}` : "",
  ]
    .filter(Boolean)
    .join(" / ");

  return (
    <div className="tour-box th-ani gsap-cursor h-100 position-relative">
      {pack.isNew && (
        <div
          style={{
            position: "absolute",
            top: "-6px",
            left: "-6px",
            width: "100px",
            height: "100px",
            overflow: "hidden",
            zIndex: 10,
          }}
        >
          <span
            style={{
              position: "absolute",
              display: "block",
              width: "140px",
              padding: "6px 0",
              backgroundColor: "var(--theme-color, #c12026)",
              color: "#fff",
              textTransform: "uppercase",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "13px",
              transform: "rotate(-45deg)",
              left: "-34px",
              top: "20px",
              boxShadow: "0 4px 8px rgba(0,0,0,0.15)",
              letterSpacing: "1px",
            }}
          >
            New
          </span>
        </div>
      )}
      <div className="tour-box_img global-img">
        {pack.packageThemesNames && pack.packageThemesNames.length > 0 && (
          <div className="tagcloud text-end" style={{ maxWidth: 300 }}>
            {pack.packageThemesNames.map((theme, i) => (
              <Link key={i} href={href} title={pack.packageName}>
                {theme}
              </Link>
            ))}
          </div>
        )}
        <Link href={href} title={pack.packageName} style={{ position: "relative", height: "250px", display: "block" }}>
          <Image
            src={pack.photoURL}
            alt={pack.packageName}
            fill
            sizes="(max-width: 576px) 100vw, (max-width: 992px) 50vw, 25vw"
            style={{ objectFit: "cover" }}
          />
          {/* "Offer" badge — bottom-left corner of the image, clear of the
              top-left "New" ribbon and the top-right theme tags. Brand-red
              gradient + white text + red glow so it stays legible on any photo. */}
          {pack.hasOffers && (
            <span
              className="ch-offer-badge"
              style={{
                position: "absolute",
                bottom: "12px",
                left: "12px",
                zIndex: 4,
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                borderRadius: "999px",
                background:
                  "linear-gradient(135deg, #ff5a5f 0%, var(--theme-color, #df1b23) 55%, #a30f15 100%)",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 800,
                lineHeight: 1,
                letterSpacing: "0.6px",
                textTransform: "uppercase",
                border: "1px solid rgba(255, 255, 255, 0.85)",
                boxShadow:
                  "0 4px 14px rgba(223, 27, 35, 0.55), 0 2px 6px rgba(0, 0, 0, 0.35)",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.35)",
              }}
            >
              <i className="fa-solid fa-fire" aria-hidden="true" />
              Special Offer
            </span>
          )}
        </Link>
      </div>
      <div className="tour-content">
        <h3 className="box-title">
          <Link href={href} title={pack.packageName}>{pack.packageName}</Link>
        </h3>
        {(showCities || pack.cruiseNights > 0) && (
          <div className="tagcloud mt-2 mb-1">
            {showCities &&
              pack.cities.map((city, j) => <span key={j}>{city}</span>)}
            {/* Cruise chip — light-grey, matches the one on the package detail
                page (under the title). Only for packages with cruise nights. */}
            {pack.cruiseNights > 0 && (
              <span style={{ backgroundColor: "#e5e7eb", color: "#000" }}>
                <i
                  className="fa-solid fa-ship"
                  style={{ marginInlineEnd: 6 }}
                  aria-hidden="true"
                />
                Cruise
              </span>
            )}
          </div>
        )}
        {!isTravelIdea && (
          <h4 className="tour-box_price">
            From
            <span className="currency ms-1">
              ${pack.startingPrice.toLocaleString()}
            </span>
          </h4>
        )}
        <div className={`tour-action ${styles.actions}`}>
          <span className={styles.duration} style={{ fontSize: "15px" }}>
            <i className="fa-light fa-clock"></i>
            {duration}
          </span>
          <Link
            href={href}
            className="th-btn style1 th-icon"
            title={`More Details about ${pack.packageName}`}
          >
            More Details
            <span className="visually-hidden">{` about ${pack.packageName}`}</span>
          </Link>
        </div>
      </div>
    </div>
  );
}
